import { Button } from "@circleco/compass/components/Button";
import type { MenuProps } from "@circleco/compass/components/Menu";
import { SplitButton } from "@circleco/compass/components/SplitButton";
import { useMemo } from "react";

export interface ActionOption {
  label: string;
  onClick?: () => void;
  value: string;
}

interface ActionSplitButtonProps {
  disabled?: boolean;
  options: ActionOption[];
  selectedAction?: ActionOption;
  setSelectedAction: (action: ActionOption) => void;
  variant: "secondary" | "danger";
}

export const ActionSplitButton = ({
  disabled = false,
  options,
  selectedAction,
  setSelectedAction,
  variant,
}: ActionSplitButtonProps) => {
  const selectedOption =
    options.find(option => option.value === selectedAction?.value) ??
    options[0];

  const menuOptions: MenuProps["options"] = useMemo(
    () =>
      options
        .filter(option => option.value !== selectedOption?.value)
        .map(option => ({
          label: option.label,
          onClick: () => {
            setSelectedAction(option);
            option.onClick?.();
          },
        })),
    [options, selectedOption?.value, setSelectedAction],
  );

  if (menuOptions.length === 0) {
    return (
      <Button
        variant={variant === "danger" ? "destructive" : "outline"}
        disabled={disabled}
        type="button"
        onClick={() => selectedOption?.onClick?.()}
        data-testid="perform-action-button"
      >
        {selectedOption?.label}
      </Button>
    );
  }

  return (
    <SplitButton
      variant={variant === "danger" ? "primary" : "outline"}
      disabled={disabled}
      type="button"
      onClick={() => selectedOption?.onClick?.()}
      menuOptions={menuOptions}
      data-testid="perform-action-button"
    >
      {selectedOption?.label}
    </SplitButton>
  );
};
